import { FC, useEffect, useState } from 'react';

import {
  SortAscendingOutlined,
  SortDescendingOutlined,
} from '@ant-design/icons';
import { Button, Input } from 'antd';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import { ReportMenu } from './reportMenu/reportMenu';
import { safeSessionStorage } from '../../../../../helpers/utils/safeStorage';
import { MenuTab } from '../../../../../app/providers/redux/slices/app/types';
import { Tag } from '../../../../../app/providers/redux/slices/reports/params/reports/types';
import { defaultAdsGroupings, defaultGroupings, defaultMetrics } from '../../../../../app/providers/redux/slices/reports/table/tableSlice';
import { getLinkPathMenu } from '../../../../../helpers/utils/functions';
import {
  parseTableData,
  handleReportPeriod,
} from '../../../../../helpers/utils/reports';
import { routeNames } from '../../../constants/routes';
import { useActions } from '../../../hooks/useActions';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import Loader from '../../loader/Loader';
import styles from '../styles/TabContent.module.scss';
import { DatePeriodType, MainType, TabType } from '../types';
import { filterByParams } from '../utils/filter-reports';

export type ContentTab = 'all' | 'my' | 'schools' | 'scan';

export type TabContentProps = {
  mainTab: MenuTab;
  subTab: 'all' | 'favorites';
  activeContentTab: ContentTab;
  onContentTabChange: (key: 'all' | 'my' | 'schools' | 'scan') => void;
  data: Array<{
    id: number;
    name: string;
    type: string;
    is_favorite: boolean;
    category?: string;
    tags?: any;
    values?: any;
    report_id: any;
    hash: any;
  }>;
  activeReportTab?: MenuTab | DatePeriodType;
  activeTagTab?: 'manager' | 'traffic' | 'funnels' | 'contractors';
  activeTab?: TabType;
  onItemClick?: () => void;
  tags: {
    dashboards: Tag[];
    reports: Tag[];
  };
  activeMainTab: MenuTab;
  collapsed: boolean;
};

export type ItemTypee = {
  id: any;
  name: string;
  type: string;
  access_id: number;
  is_favorite: boolean;
  user_id: number;
  category?: string;
  tags?: Array<{ id: number; name: string }>;
  values?: any;
  report_id?: any;
  hash?: any;
  static?: true;
};

const getItemClassName = (isSelect: boolean) =>
  `${styles.linkItem}${isSelect ? ` ${styles.active}` : ''}`;

const MainTabInReportRoute = {
  ['registrations']: 1,
  ['orders']: 2,
  ['payments']: 3,
  ['ads']: 4,
  ['webinars']: 5,
};

export const getRouteByReportId = (reportId: number) => {
  switch (reportId) {
    case 1:
      return routeNames.home;
    case 2:
      return routeNames.orderReports;
    case 3:
      return routeNames.paymentReports;
    case 4:
      return routeNames.adReports;
    case 5:
      return routeNames.webinarReports;
    default:
      return routeNames.home;
  }
};

export const KEY_WEBINAR_QUALITY = 'webinarQuality';
const webinarQuality: ItemTypee = {
  id: KEY_WEBINAR_QUALITY,
  hash: KEY_WEBINAR_QUALITY + 'hash',
  name: 'Качество вебинаров',
  report_id: 5,
  type: '3',
  access_id: 3,
  is_favorite: false,
  user_id: -1,
  static: true,
};
export const KEY_COHGORT = 'cohgort';
const cohgort: ItemTypee = {
  id: KEY_COHGORT,
  hash: KEY_COHGORT + 'hash',
  name: 'Когортный анализ',
  report_id: 3,
  type: '3',
  access_id: 3,
  is_favorite: false,
  user_id: -1,
  static: true,
};

const TabContent: React.FC<TabContentProps> = ({
  mainTab,
  subTab,
  activeContentTab,
  onContentTabChange,
  data,
  onItemClick,
  activeTab,
  tags,
  collapsed,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ searchParams, setSearchParams ] = useSearchParams();
  
  const [ searchQuery, setSearchQuery ] = useState('');
  const [ sortOrder, setSortOrder ] = useState<'asc' | 'desc'>('asc');

  const loadingReport = useTypedSelector(state => state.reports.isLoadingReports);
  const savedReports = useTypedSelector(state => state.reports.savedReports);
  const savedDashboards = useTypedSelector(state => state.reports.savedDashboards);
  const userAccess = useTypedSelector(state => state.currentSchool.userAccess);
  const role = useTypedSelector(state => state.currentUser.role);

  const {
    setCurrentDashboard,
    setCurrentReport,
    setMetaCompare,
    setMetrics,
    setGroupings,
    setFilters,
    setFiltersTable,
    setSortTable,
    setCurrentFunnel,
    setCurrentSegment,
    setMetricsChecked,
    setTableClearState,
    setChartClearState,
    setAllDataFetching,
    setMetaTableLoading,
    setMetaChartLoading,
  } = useActions();

  const isReport = mainTab === 'reports';

  const tabButtons = [
    { key: 'all', label: 'Все' },
    { key: 'my', label: 'Мои' },
    { key: 'schools', label: 'Школы' },
    { key: 'scan', label: 'SCAN' },
  ];

  const handleTabChange = (activeKey: string) => {
    onContentTabChange(activeKey as 'all' | 'my' | 'schools' | 'scan');
  };

  const handleSort = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const currentData = isReport ? [ ...savedReports, webinarQuality, cohgort ] : savedDashboards;
  const currentItemHash =  new URLSearchParams(location.search).get('report_id');

  const isWebinarQuality = window.location.pathname.includes(routeNames.reportWebinarQuality);
  const isCohort = window.location.pathname.includes(routeNames.reportCohort);

  const getFilteredData = (contentTab: ContentTab) => {
    return currentData
      .filter((item) =>
        filterByParams(
          item,
          activeTab,
          subTab,
          mainTab,
          tags,
          contentTab,
          searchQuery
        )
      )
      .sort((a, b) =>
        sortOrder === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name)
      );
  };

  const handleItemClick = (item: ItemTypee) => {

    if (item.id === KEY_WEBINAR_QUALITY) {
      // Проверяем, находимся ли мы уже на этой странице
      if (location.pathname === routeNames.reportWebinarQuality) {
        // Просто перезагружаем данные
        setAllDataFetching({ value: true });
        onItemClick && onItemClick();
        return;
      }
      navigate(routeNames.reportWebinarQuality);
      if (onItemClick) onItemClick();
      return;
    }

    if (item.id === KEY_COHGORT) {
      // Проверяем, находимся ли мы уже на этой странице
      if (location.pathname === routeNames.reportCohort) {
        // Просто перезагружаем данные
        setAllDataFetching({ value: true });
        onItemClick && onItemClick();
        return;
      }
      navigate(routeNames.reportCohort);
      if (onItemClick) onItemClick();
      return;
    }

    const targetRoute = getRouteByReportId(item.report_id);
    
    console.log('[TabContent] handleItemClick check:', {
      locationPathname: location.pathname,
      targetRoute,
      currentItemHash,
      itemHash: item.hash,
      pathMatch: location.pathname === targetRoute,
      hashMatch: currentItemHash === item.hash
    });
    
    // Проверяем, находимся ли мы уже на этом отчёте (тот же route + тот же report_id)
    // Используем currentItemHash для консистентности с определением выбранного элемента
    if (location.pathname === targetRoute && currentItemHash === item.hash) {
      // Просто перезагружаем данные отчёта
      console.log('[TabContent] Уже на этом отчёте, перезагружаем данные');
      setAllDataFetching({ value: true });
      onItemClick && onItemClick();
      return;
    }
    
    const report = data.find((rep: any) => rep.id === item.id);
    if (!report) return;
    
    navigate(`${targetRoute}?report_id=${item.hash}`, {
      state: { report_id: item.hash },
    });

    onItemClick && onItemClick();
  };

  const activateReport = async (item: ItemTypee) => {
    const id = item.id.toString();
    safeSessionStorage.setItem('dashboard_id', id);
    safeSessionStorage.setItem('dashboard_hash', item.hash);
    const path = getLinkPathMenu(routeNames.dashboards, userAccess, role);
    
    // Проверяем, находимся ли мы уже на этом дашборде (тот же route + тот же report_id)
    // Используем currentItemHash для консистентности
    if (location.pathname === path && currentItemHash === item.hash) {
      // Просто перезагружаем данные дашборда
      setAllDataFetching({ value: true });
      if (onItemClick) {
        onItemClick();
      }
      return;
    }
    
    navigate(`${path}?report_id=${item.hash}`);

    if (onItemClick) {
      onItemClick();
    }
  };

  const handleCreateReport = () => {
    const targetRoute = getRouteByReportId(MainTabInReportRoute?.[activeTab as keyof typeof MainTabInReportRoute]);
    
    // Проверяем, находимся ли мы уже на странице нового отчёта этого типа (без report_id)
    // Используем currentItemHash для консистентности
    if (mainTab === 'reports' && location.pathname === targetRoute && !currentItemHash) {
      // Уже на странице нового отчёта — просто перезагружаем данные
      console.log('[TabContent] Уже на странице нового отчёта, перезагружаем данные');
      setAllDataFetching({ value: true });
      onItemClick && onItemClick();
      return;
    }
    
    // Проверяем для дашбордов
    if (mainTab === 'dashboards' && location.pathname === routeNames.dashboards && !currentItemHash) {
      // Уже на странице нового дашборда — просто перезагружаем данные
      console.log('[TabContent] Уже на странице нового дашборда, перезагружаем данные');
      setAllDataFetching({ value: true });
      onItemClick && onItemClick();
      return;
    }
    
    const groupingsToSet = activeTab === 'ads' ? defaultAdsGroupings : defaultGroupings;

      // Сначала включаем лоадеры, чтобы пользователь не видел пустую таблицу
      setMetaTableLoading({ value: true });
      setMetaChartLoading({ value: true });
      
      setMetaCompare({ value: false });
      setMetrics({ data: defaultMetrics });
      setGroupings({ data: groupingsToSet });
      setFilters([]);
      setFiltersTable([]);
      setSortTable({});
      setCurrentFunnel([]);
      setCurrentSegment(null);
      setMetricsChecked({ data: [] });
      setSearchParams({});
      setTableClearState();
      setChartClearState();
    if(mainTab === 'reports'){
      setCurrentReport({});
      navigate(targetRoute);
      onItemClick && onItemClick();
      return;
    }
    if(mainTab === 'dashboards'){
      setCurrentDashboard({});
      navigate(routeNames.dashboards);
      onItemClick && onItemClick();
      return;
    }
  };

  return (
    <div
      className={`${styles['tab-content']} ${
        collapsed ? styles['tab-content-collapsed'] : ''
      }`}
      style={{ width: 460 }}
    >
      {(
        <div>
          <div className={styles['tab-buttons']}>
            {tabButtons.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleTabChange(key)}
                className={`${styles.filterTab} ${activeContentTab === key ? styles.filterTabActive : ''}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div>
            <div style={{ display: 'flex', gap: '8px', paddingRight: '15px', marginBottom: 12 }}>
              <Input
                placeholder="Поиск..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={styles.searchInput}
              />
              <Button
                onClick={handleSort}
                className={styles.sortButton}
                icon={
                  sortOrder === 'asc' ? (
                    <SortDescendingOutlined style={{ fontSize: '20px' }} />
                  ) : (
                    <SortAscendingOutlined style={{ fontSize: '20px' }} />
                  )
                }
              />
            </div>
            <Loader loading={loadingReport} hasTip>
              {getFilteredData(activeContentTab).map((item) => {

                const isSelect = currentItemHash === item.hash
                  || (isWebinarQuality && item.id === KEY_WEBINAR_QUALITY)
                  || (isCohort && item.id === KEY_COHGORT);

                return (
                  <div
                    key={item.id}
                    className={getItemClassName(isSelect)}
                  >
                    <a
                      href={item.hash.includes('webinarQuality')? '/webinar/quality' : item.hash.includes('cohgort')? '/users/cohort' : `${mainTab === 'reports'? getRouteByReportId(item.report_id): routeNames.dashboards}?report_id=${item.hash}`}
                      onClick={(event) => {
                        event.preventDefault();
                        if (mainTab === 'reports') {
                          handleItemClick(item);
                        } else if (mainTab === 'dashboards') {
                          activateReport(item);
                        }
                      }}
                    >
                      {item.name} {item.is_favorite && <span className={styles.star}>★</span>}
                    </a>
                    <div onClick={(e) => e.stopPropagation()}>
                      <ReportMenu
                        item={item}
                        isDashboard={mainTab === 'dashboards'}
                      />
                    </div>
                  </div>
                );
              })}
            </Loader>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabContent;
